import { c as _c } from "react/compiler-runtime";
import Skeleton from '@precis-digital/kurama/Skeleton';
import { styled } from '@precis-digital/kurama/utils';
import type { Row } from '@tanstack/react-table';
import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import type { ReactElement } from 'react';
import type { ChartDataRow } from '../../../types';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import TableBodyCell from './TableBodyCell';
import { useHovered } from '../hooks';
import type { TableBodyBaseProps } from '../types';
import { getPinPositionForColumnId } from '../utils';
import { StyledTableVirtualBodyRow } from '../styled';
interface TableBodyRowProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends Omit<TableBodyBaseProps<DataKey, DataRow>, 'table'> {
  row: Row<DataRow>;
  virtualRow: VirtualItem;
  rowVirtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
}
const TableBodyRow = t0 => {
  const $ = _c(35);
  const {
    row,
    virtualRow,
    totalsAndRangeData,
    elementsDescriptionsMap,
    colorScaleColumnMap,
    rowSelection,
    columnPinning,
    rowVirtualizer,
    allowColumnResizing,
    enableNullDimensionValue,
    isFetchLoading,
    t
  } = t0;
  const defaults = useChartCardDefaults();
  const {
    hovered,
    onMouseEnter,
    onMouseLeave
  } = useHovered();
  const isLoading = isFetchLoading === true;
  let t1;
  let t2;
  if ($[0] !== allowColumnResizing || $[1] !== colorScaleColumnMap || $[2] !== columnPinning || $[3] !== defaults || $[4] !== elementsDescriptionsMap || $[5] !== enableNullDimensionValue || $[6] !== hovered || $[7] !== isLoading || $[8] !== onMouseEnter || $[9] !== onMouseLeave || $[10] !== row || $[11] !== rowSelection || $[12] !== rowVirtualizer || $[13] !== t || $[14] !== totalsAndRangeData || $[15] !== virtualRow.index || $[16] !== virtualRow.start) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleCells = row.getVisibleCells();
      if (isLoading) {
        const rowWidth = visibleCells.reduce(_temp, 0);
        let t3;
        if ($[19] !== rowVirtualizer) {
          t3 = node => {
            rowVirtualizer.measureElement(node);
          };
          $[19] = rowVirtualizer;
          $[20] = t3;
        } else {
          t3 = $[20];
        }
        let t4;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <Skeleton height={40} />;
          $[21] = t4;
        } else {
          t4 = $[21];
        }
        t2 = <StyledTableVirtualBodyRow key={row.id} data-index={virtualRow.index} rowTop={virtualRow.start} ref={t3} hovered={false} defaults={defaults}><StyledRowSkeleton width={rowWidth}>{t4}</StyledRowSkeleton></StyledTableVirtualBodyRow>;
        break bb0;
      }
      let t3;
      if ($[22] !== rowVirtualizer) {
        t3 = node_0 => {
          rowVirtualizer.measureElement(node_0);
        };
        $[22] = rowVirtualizer;
        $[23] = t3;
      } else {
        t3 = $[23];
      }
      let t4;
      if ($[24] !== allowColumnResizing || $[25] !== colorScaleColumnMap || $[26] !== columnPinning || $[27] !== elementsDescriptionsMap || $[28] !== enableNullDimensionValue || $[29] !== hovered || $[30] !== row.id || $[31] !== rowSelection || $[32] !== t || $[33] !== totalsAndRangeData) {
        t4 = cell_0 => {
          const pinned = getPinPositionForColumnId(columnPinning, cell_0.column.id);
          const rowSelected = rowSelection[row.id] ?? false;
          return <TableBodyCell key={cell_0.id} cell={cell_0} pinned={pinned} rowSelected={rowSelected} colorScale={colorScaleColumnMap[cell_0.column.id]} rowHovered={hovered} totalsAndRangeData={totalsAndRangeData} allowColumnResizing={allowColumnResizing} enableNullDimensionValue={enableNullDimensionValue} elementsDescriptionsMap={elementsDescriptionsMap} t={t} />;
        };
        $[24] = allowColumnResizing;
        $[25] = colorScaleColumnMap;
        $[26] = columnPinning;
        $[27] = elementsDescriptionsMap;
        $[28] = enableNullDimensionValue;
        $[29] = hovered;
        $[30] = row.id;
        $[31] = rowSelection;
        $[32] = t;
        $[33] = totalsAndRangeData;
        $[34] = t4;
      } else {
        t4 = $[34];
      }
      t1 = <StyledTableVirtualBodyRow key={row.id} data-index={virtualRow.index} rowTop={virtualRow.start} ref={t3} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} hovered={hovered} defaults={defaults}>{visibleCells.map(t4)}</StyledTableVirtualBodyRow>;
    }
    $[0] = allowColumnResizing;
    $[1] = colorScaleColumnMap;
    $[2] = columnPinning;
    $[3] = defaults;
    $[4] = elementsDescriptionsMap;
    $[5] = enableNullDimensionValue;
    $[6] = hovered;
    $[7] = isLoading;
    $[8] = onMouseEnter;
    $[9] = onMouseLeave;
    $[10] = row;
    $[11] = rowSelection;
    $[12] = rowVirtualizer;
    $[13] = t;
    $[14] = totalsAndRangeData;
    $[15] = virtualRow.index;
    $[16] = virtualRow.start;
    $[17] = t1;
    $[18] = t2;
  } else {
    t1 = $[17];
    t2 = $[18];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
};

/** Matches ReportTable loader row: 48px cell height containing a 40px full-width skeleton. */
const StyledRowSkeleton = styled('div', {
  shouldForwardProp: prop => prop !== 'width'
})<{
  width: number;
}>(({
  theme,
  width
}) => ({
  display: 'flex',
  flex: 1,
  width,
  alignItems: 'center',
  minHeight: theme.spacing(8),
  boxSizing: 'border-box'
}));
export default TableBodyRow;
function _temp(sum, cell) {
  return sum + cell.column.getSize();
}